import { useCallback, useEffect, useState } from 'react';
import { useAuth } from '@/hooks/useAuth';
import {
  listMerchantPaymentAccess,
  setMerchantPaymentAccess,
  type MerchantAccessRow,
} from '@/lib/merchantPaymentAccess';
import { avatarOr } from '@/lib/defaultImages';

// ─────────────────────────────────────────────────────────────
// SuperAdmin — Accès au paiement en ligne par commerce.
// Recherchez un commerce puis autorisez (ou retirez) le paiement
// Mobile Money de ses clients sur sa boutique.
// ─────────────────────────────────────────────────────────────

export default function SuperAdminCommercePaymentsPage() {
  const { user } = useAuth();
  const [search, setSearch] = useState('');
  const [rows, setRows] = useState<MerchantAccessRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<number | null>(null);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const load = useCallback(async (query: string) => {
    setLoading(true);
    setError('');
    try {
      setRows(await listMerchantPaymentAccess(query, 60));
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Chargement impossible.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const timer = window.setTimeout(() => { void load(''); }, 0);
    return () => window.clearTimeout(timer);
  }, [load]);

  const toggle = async (row: MerchantAccessRow) => {
    setSaving(row.commerceId);
    setError('');
    try {
      await setMerchantPaymentAccess({
        commerceId: row.commerceId,
        enabled: !row.enabled,
        commissionPercent: row.commissionPercent,
        note: row.note,
        updatedBy: user?.id ?? null,
      });
      setRows((prev) => prev.map((r) => (r.commerceId === row.commerceId ? { ...r, enabled: !r.enabled } : r)));
      setNotice(!row.enabled
        ? `Paiement en ligne autorisé pour « ${row.name} ».`
        : `Paiement en ligne retiré pour « ${row.name} ».`);
      window.setTimeout(() => setNotice(''), 3500);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Modification impossible.');
    } finally {
      setSaving(null);
    }
  };

  const submitSearch = (e: React.FormEvent) => {
    e.preventDefault();
    void load(search);
  };

  return (
    <div className="p-4 md:p-6">
      <div className="mb-6">
        <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950">
          <i className="ri-store-3-line mr-2 text-primary-500"></i>Paiement des commerces
        </h2>
        <p className="text-sm text-foreground-500 mt-1">
          Autorisez un commerce à encaisser les paiements Mobile Money de ses clients.
        </p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 text-red-600 text-sm flex items-center gap-2">
          <i className="ri-error-warning-line"></i>{error}
          <button onClick={() => setError('')} className="ml-auto cursor-pointer"><i className="ri-close-line"></i></button>
        </div>
      )}
      {notice && (
        <div className="mb-4 px-4 py-3 rounded-lg bg-accent-50 text-accent-700 text-sm flex items-center gap-2">
          <i className="ri-checkbox-circle-line"></i>{notice}
        </div>
      )}

      <form onSubmit={submitSearch} className="mb-5 flex flex-col sm:flex-row gap-2">
        <div className="relative flex-1">
          <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400"></i>
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher un commerce (nom, pseudo, email)…"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg border border-background-200/70 bg-background-50 text-sm text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-300"
          />
        </div>
        <button
          type="submit"
          className="px-5 py-2.5 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
        >
          Rechercher
        </button>
      </form>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
        </div>
      ) : rows.length === 0 ? (
        <div className="bg-background-50 border border-background-200/70 rounded-lg py-16 text-center">
          <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mx-auto mb-3">
            <i className="ri-store-3-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-sm text-foreground-500">Aucun commerce trouvé.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {rows.map((row) => (
            <div key={row.commerceId} className="bg-background-50 border border-background-200/70 rounded-lg p-3.5 flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-background-100 overflow-hidden flex-shrink-0">
                <img src={avatarOr(row.photo)} alt={row.name} className="w-full h-full object-cover" />
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-semibold text-foreground-900 truncate">{row.name}</p>
                <p className="text-[11px] text-foreground-500 truncate">
                  {row.userName ? `@${row.userName}` : row.email || `#${row.commerceId}`}
                  {row.currency ? ` · ${row.currency}` : ''}
                </p>
              </div>
              <span className={`px-2.5 py-1 rounded-full text-[10px] font-semibold whitespace-nowrap ${
                row.enabled ? 'bg-accent-100 text-accent-900' : 'bg-background-100 text-foreground-500'
              }`}>
                {row.enabled ? 'Paiement autorisé' : 'Non autorisé'}
              </span>
              <button
                type="button"
                onClick={() => { void toggle(row); }}
                disabled={saving === row.commerceId}
                className={`px-3.5 py-2 rounded-full text-xs font-semibold whitespace-nowrap transition-colors cursor-pointer disabled:opacity-50 ${
                  row.enabled
                    ? 'bg-red-50 text-red-600 hover:bg-red-100'
                    : 'bg-primary-500 text-background-50 hover:bg-primary-600'
                }`}
              >
                {saving === row.commerceId
                  ? <i className="ri-loader-4-line animate-spin"></i>
                  : row.enabled ? 'Retirer' : 'Autoriser'}
              </button>
            </div>
          ))}
        </div>
      )}

      <p className="text-[11px] text-foreground-400 mt-4 leading-relaxed flex items-start gap-1.5">
        <i className="ri-information-line mt-0.5"></i>
        Quand la portée des paiements est réglée sur « Abonnements Zifek uniquement » (page Paiements
        plateforme), seuls les commerces autorisés ici peuvent encaisser en ligne.
      </p>
    </div>
  );
}